/**
 * check if child element is child from parent element
 *
 * @param parent
 * @param child
 * @returns {boolean}
 */
import {isComponentName} from '@/library/utils';
import {type ComponentPublicInstance} from 'vue';

export function isParent(parent: HTMLElement | Element, child: HTMLElement | Element)
{
    let node = child.parentNode;
    while (node != null)
    {
        if (node === parent)
        {
            return true;
        }
        node = node.parentNode;
    }

    return false;
}

export function getOuterDimensions(el: HTMLElement)
{
    let styles = window.getComputedStyle(el);

    return {
        height: el.offsetHeight + ~~styles.marginTop + ~~styles.marginBottom,
        width: el.offsetWidth + ~~styles.marginLeft + ~~styles.marginRight,
    };
}

export function getElementOffsets(el: HTMLElement, fixed = true)
{
    let rect = el.getBoundingClientRect();

    return {
        top: Math.round(rect.top + (fixed ? 0 : window.scrollY || window.pageYOffset)),
        left: Math.round(rect.left + (fixed ? 0 : window.scrollX || window.pageXOffset)),
    };
}

export function setStyles(el: HTMLElement, styles: object)
{
    let important = false;
    for (let property in styles)
    {
        if (styles.hasOwnProperty(property))
        {
            if (/ !important$/.test(styles[property]))
            {
                important        = true;
                styles[property] = styles[property].replace(/ !important$/, '');
            }
            else
            {
                important = false;
            }

            // for a set style property with priority = important, some browsers are
            // not able to overwrite it with a property != important; removing the
            // property first solves this issue
            if (el.style.getPropertyPriority(property) === 'important' && !important)
            {
                el.style.removeProperty(property);
            }

            el.style.setProperty(property, styles[property], (important ? 'important' : ''));
        }
    }
}

export function setAlignment(tooltipEl: HTMLElement, targetEl: HTMLElement, alignmentOptions: Partial<AlignmentOptions>)
{
    let options: AlignmentOptions = Object.assign({
        position: 'bottom-right',
        forcePosition: false,
        keepOnScreen: false,
        fixed: false,
        arrow: false,
        preventHeaderOverlap: false,
    }, alignmentOptions);

    tooltipEl.style.removeProperty('bottom');
    setStyles(tooltipEl, {
        left: '0',
        top: '0',
        visibility: 'hidden',
    });

    let position: string = options.position;
    let targetOff        = getElementOffsets(targetEl, options.fixed);
    let targetW          = targetEl.offsetWidth;
    let targetH          = targetEl.offsetHeight;
    let win              = {
        width: document.documentElement.clientWidth || document.body.clientWidth,
        height: window.innerHeight,
        scrollLeft: options.fixed ? 0 : document.documentElement.scrollLeft,
        scrollTop: options.fixed ? 0 : document.documentElement.scrollTop,
    };
    let target           = {
        left: targetOff.left,
        top: targetOff.top,
        right: targetOff.left + targetW,
        bottom: targetOff.top + targetH,
        width: targetW,
        height: targetH,
    };
    let tooltip          = {
        left: 0,
        top: 0,
        width: tooltipEl.offsetWidth,
        height: tooltipEl.offsetHeight,
    };
    let targetDistance   = options.arrow ? 12 : 0;
    let screenMargin     = 0;

    // center tooltip on target
    tooltip.left = target.left + (target.width - tooltip.width) / 2;
    tooltip.top  = target.top + (target.height - tooltip.height) / 2;

    // default - auto calculate
    if (position === 'default' || position === 'auto' || options.keepOnScreen)
    {
        // assuming normal position - above target
        tooltip.left = target.left + (target.width - tooltip.width) / 2;
        tooltip.top  = target.top - tooltip.height - targetDistance;
        position     = '';

        // too far to the top - put tooltip below element
        if (win.scrollTop > tooltip.top)
        {
            tooltip.top = target.top + target.height + targetDistance;
            position    = 'bottom';
        }
        else
        {
            position = 'top';
        }

        // too far to the left - put tooltip to the right of the target
        if (tooltip.left < screenMargin + win.scrollLeft)
        {
            tooltip.left = screenMargin + win.scrollLeft;
            position += '-left';
        }

        // too far to the right - put tooltip to the left of the target
        if (tooltip.left + tooltip.width + screenMargin - win.scrollLeft > win.width)
        {
            tooltip.left = win.width - tooltip.width - screenMargin + win.scrollLeft;
            position += '-right';
            if (tooltip.left < target.left - tooltip.width)
            {
                // keep tooltip on target
                tooltip.left = target.left - tooltip.width;
            }
        }
    }

    let arrowBorder   = '';
    let arrowPosition = {
        left: tooltip.width / 2 - 10,
        top: tooltip.height / 2 - 10,
    };
    let positionSplit = position.split('-');
    // position the tooltip
    switch (positionSplit[0])
    {
        case 'top':
            tooltip.top       = target.top - tooltip.height - targetDistance;
            arrowPosition.top = tooltip.height - 1;
            arrowBorder       = 'top';
            break;
        case 'bottom':
            tooltip.top       = target.bottom + targetDistance;
            arrowBorder       = 'bottom';
            arrowPosition.top = -20 + 1;
            break;
        case 'left':
            tooltip.left       = target.left - tooltip.width - targetDistance;
            arrowPosition.left = tooltip.width - 1;
            arrowBorder        = 'left';
            break;
        case 'right':
            tooltip.left       = target.right + targetDistance;
            arrowPosition.left = -20 + 1;
            arrowBorder        = 'right';
            break;
    }

    if (positionSplit[1])
    {
        switch (positionSplit[1])
        {
            case 'top':
                // rb/lb
                tooltip.top       = target.top;
                arrowPosition.top = target.height / 2 - 10;
                // tooltip.top = target.top + target.height / 2 /*- 10*/;
                break;
            case 'bottom':
                // tooltip.top = target.bottom - tooltip.height - target.height / 2/* + 10*/;
                tooltip.top       = target.bottom - tooltip.height/* + 10*/;
                arrowPosition.top = tooltip.height - target.height / 2 - 10;
                break;
            case 'left':
                // br/tr
                tooltip.left       = target.left;
                arrowPosition.left = target.width / 2 - 10;
                // tooltip.left = target.left + target.width / 2/* - 10*/;
                break;
            case 'right':
                tooltip.left       = target.right - tooltip.width;
                arrowPosition.left = tooltip.width - target.width / 2 - 10;
                // tooltip.left = target.right - tooltip.width - target.width / 2 /*+ 10*/;
                break;
        }
    }

    let navigation         = document.getElementById('left')?.getElementsByTagName('nav');
    let marginToNavigation = 15;
    let navigationWidth    = navigation && navigation.length ? (navigation[0].clientWidth + marginToNavigation) : 0;

    // ensure tooltip isn't out of bounds
    if (tooltip.left < navigationWidth)
    {
        arrowPosition.left = tooltip.width / 2 - 10;
        tooltip.left       = navigationWidth;
    }

    if (tooltip.width > win.width)
    {
        tooltip.width = win.width;
        arrowPosition.left -= tooltip.left;
        tooltip.left  = 0;
    }

    let arrowElement = tooltipEl.querySelector('.popover-arrow') as HTMLElement;
    if (options.arrow && arrowElement)
    {
        setStyles(arrowElement, {
            'border-top-color': '',
            'border-bottom-color': '',
            'border-left-color': '',
            'border-right-color': '',
            ['border-' + arrowBorder + '-color']: getComputedStyle(tooltipEl)['background-color'],
            'border-width': '10px',
            top: tooltip.top + arrowPosition.top + 'px',
            left: tooltip.left + arrowPosition.left + 'px',
        });
    }

    let headerHeight = 80;
    if (process.env.NODE_ENV === 'styleguide')
    {
        headerHeight = 120;
    }

    setStyles(tooltipEl, {
        left: tooltip.left + 'px',
        top: (options.preventHeaderOverlap && tooltip.top < headerHeight ? headerHeight : tooltip.top) + 'px',
    });

    if (options.fixed)
    {
        if (tooltip.top < win.height / 2 && tooltip.top + tooltip.height > win.height)
        {
            tooltipEl.style.bottom = '0';
            options.keepOnScreen   = true; // cancel re-position
        }
    }

    // if forcePosition != true -> check if on screen and realign if necessary
    if (!options.forcePosition && !options.keepOnScreen)
    {
        let isOnScreen = true;

        // above screen
        if (tooltip.top + screenMargin < win.scrollTop)
        {
            isOnScreen = false;
        }
        // below screen
        if (tooltip.top + tooltip.height + screenMargin - win.scrollTop > win.height)
        {
            isOnScreen = false;
        }

        // too far to the left
        if (tooltip.left < screenMargin + win.scrollLeft)
        {
            isOnScreen = false;
        }

        // too far to the right
        if (tooltip.left + tooltip.width + screenMargin - win.scrollLeft > win.width)
        {
            isOnScreen = false;
        }

        if (!isOnScreen)
        {
            setAlignment(tooltipEl, targetEl, Object.assign({}, options, {keepOnScreen: true}));
            return;
        }
    }

    if (options.fixed)
    {
        if (tooltip.top + tooltip.height > win.height)
        {
            tooltipEl.style.bottom = '0';
        }
        if (tooltip.top < 0)
        {
            tooltipEl.style.top = options.preventHeaderOverlap ? headerHeight + 'px' : '0';
        }
    }

    tooltipEl.style.removeProperty('visibility');
}

export function getOffsetToBody(element: HTMLElement): { top: number, left: number }
{
    let top    = 0;
    let left   = 0;
    let parent = element.offsetParent as HTMLElement;
    while (parent)
    {
        let computedStyle = window.getComputedStyle(parent, null);

        top += parent.offsetTop + (!top || 'body' !== parent.nodeName.toLowerCase() ? parseInt(computedStyle.getPropertyValue('padding-top')) : 0);
        left += parent.offsetLeft;

        parent = parent.offsetParent as HTMLElement;
    }

    return {
        top,
        left,
    };
}

export function scrollToElement(element: HTMLElement): void
{
    window.scrollTo(0, getOffsetToBody(element).top - 100);
}

export function scrollToElementBySelector(selector: string): void
{
    const element = document.querySelector(selector) as Nullable<HTMLElement>;
    window.scrollTo(0, element ? getOffsetToBody(element).top - 100 : 0);
}

export function scrollToContentElement(element: HTMLElement | Element, header: Nullable<HTMLElement> = null): void
{
    header = header || document.querySelector('#page-header') as HTMLElement;

    let headerOffset = 0;

    if (header)
    {
        headerOffset = header.offsetHeight;
    }

    window.scrollTo({
        top: element.getBoundingClientRect().top - document.body.getBoundingClientRect().top - headerOffset - 10,
        behavior: 'smooth',
    });
}

export function identifyFixedHeaderElement(excludeElements: HTMLElement[] = []): Nullable<HTMLElement>
{
    return searchElement(element =>
    {
        for (const exclude of excludeElements)
        {
            if (exclude.contains(element))
            {
                return false;
            }
        }

        const style = window.getComputedStyle(element);

        return style.getPropertyValue('position') === 'fixed'
            && element.getBoundingClientRect().top === 0
            && parseInt(style.getPropertyValue('z-index')) > 1;
    });
}

export function searchElement(validateCallback: (element: HTMLElement) => boolean, context: Element = document.body): Nullable<HTMLElement>
{
    for (const child of [...context.children] as HTMLElement[])
    {
        if (validateCallback(child))
        {
            return child;
        }

        let element = searchElement(validateCallback, child);

        if (element)
        {
            return element;
        }
    }

    return null;
}

export function scrollToTop(): void
{
    window.scrollTo(0, 0);
}

function triggerEvent(element: HTMLElement, eventName: string, data = null)
{
    let event;
    if (data !== null)
    {
        if (window.CustomEvent && typeof window.CustomEvent === 'function')
        {
            event = new CustomEvent(eventName, {detail: data});
        }
        else
        {
            event = document.createEvent('CustomEvent');
            event.initCustomEvent(eventName, true, true, data);
        }
    }
    else
    {
        event = document.createEvent('HTMLEvents');
        event.initEvent(eventName, true, false);
    }

    element.dispatchEvent(event);
}

export function htmlToText(html: string)
{
    const div     = document.createElement('div');
    div.innerHTML = html;

    return div.innerText.trim();
}

export function getVueParent(vueInstance: ComponentPublicInstance, parentComponentName: string)
{
    let vueNode: Nullable<Merge<ComponentPublicInstance, { componentName?: string }>> = vueInstance.$parent;

    while (vueNode)
    {
        if (isComponentName(vueNode, parentComponentName))
        {
            return vueNode;
        }

        vueNode = vueNode.$parent;
    }

    return null;
}

export function isDisabled(element: HTMLElement)
{
    if (element.classList.contains('disabled'))
    {
        return true;
    }

    return null !== element.getAttribute('disabled');
}

export function isVisible(element: HTMLElement)
{
    function checkVisibility(element)
    {
        const style = window.getComputedStyle(element);

        return style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0;
    }

    let checkElement = element;

    while (checkElement)
    {
        if (!checkVisibility(checkElement))
        {
            return false;
        }

        checkElement = checkElement.parentElement;
    }

    return true;
}

export {
    triggerEvent,
};